import {zodResolver} from '@hookform/resolvers/zod';
import React, {Fragment, useCallback, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {FormProvider, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';

import {AlertActions, AlertModal, StyledGrid} from 'presentation/components';
import {FooterMobile} from 'presentation/components/MainFooter/FooterMobile';
import {SignInEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useMediaQueries} from 'presentation/pages/private';
import {translations} from 'presentation/translations-new';
import {SectionContent, StyledBox} from '../styles';
import {FirstAccessForm, Header} from './components';
import {IFormData, formSchema} from './components/FirstAccessForm/schema';

const {cancel, exit} = translations['pt-br'].commons;
const {cancelFlowModal} = translations['pt-br'].publicFlow.signUp;
const {headSubtitle, headTitle} = translations['pt-br'].publicFlow.firstAccess;

export function FirstAccess() {
	const methods = useForm<IFormData>({
		defaultValues: {
			email: '',
			cellphone: '',
			password: '',
			repeatPassword: '',
		},
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		delayError: 1000,
	});
	const {
		formState: {isDirty},
	} = methods;
	const navigate = useNavigate();
	const {dataHostNameState} = useHostName();
	const [confirmExitFlowModalState, setConfirmExitFlowModalState] = useState(false);
	const {isTablet} = useMediaQueries();

	const {product, productName} = dataHostNameState;
	const title = `${headTitle} | ${productName}`;
	const headerSubtitle = headSubtitle[product as keyof typeof headSubtitle];

	const handleExitFlow = useCallback(() => {
		setConfirmExitFlowModalState(false);
		navigate(SignInEnum.SIGN_IN, {state: {goToSignInState: true}});
	}, []);

	const handleCancelExitFlow = useCallback(() => {
		setConfirmExitFlowModalState(false);
	}, []);

	const handleOpenConfirmExitModal = useCallback(() => {
		if (isDirty) {
			setConfirmExitFlowModalState(true);
		} else {
			navigate(SignInEnum.SIGN_IN, {state: {goToSignInState: true}});
		}
	}, [isDirty]);

	return (
		<Fragment>
			<Helmet>
				<title>{title}</title>
			</Helmet>

			<StyledGrid.Root>
				<StyledGrid.Item sm={12} md={12} lg={12}>
					<FormProvider {...methods}>
						<Header
							headerTitle={headTitle}
							headerSubtitle={headerSubtitle}
							onClickBackButton={handleOpenConfirmExitModal}
						/>
						<SectionContent>
							<FirstAccessForm />
						</SectionContent>
					</FormProvider>
				</StyledGrid.Item>
			</StyledGrid.Root>

			<StyledBox>
				<FooterMobile hideANS={!isTablet} hidePolicy />
			</StyledBox>

			<AlertModal
				variant="warning"
				open={confirmExitFlowModalState}
				onClose={handleCancelExitFlow}
				headerTitle={cancelFlowModal.headerTitle}
				title={cancelFlowModal.title}
				description={cancelFlowModal.description}
				footerContent={
					<AlertActions
						primaryAction={handleExitFlow}
						primaryText={exit}
						secondaryAction={handleCancelExitFlow}
						secondaryText={cancel}
					/>
				}
			/>
		</Fragment>
	);
}
